import { useState, useEffect } from "react";
import { Button, Spin, Typography, notification, ConfigProvider } from "antd";
import { ThemeProvider } from "styled-components";
import { SaveOutlined } from "@ant-design/icons";
import { ThemeColors } from "./types";
import { COLOR_GROUPS, initialTheme } from "./constants";
import { useKioskConfiguration } from "./hooks/useKioskConfiguration";
import { useSaveKioskConfiguration } from "./hooks/useKioskConfigurationMutations";
import { useTheme } from "../../context/ThemeContext";
import { getAntdThemeConfig } from "../../utils/antdTheme";
import ColorField from "./ColorField";
import KioskPreview from "./KioskPreview";
import StoreSwitcher from "../../commonComponents/StoreSwitcher";
import {
  PageWrapper,
  LeftPanel,
  ScrollableGroups,
  PanelHeader,
  GroupCard,
  GroupHeader,
  GroupAccentBar,
  GroupTitle,
  GroupFieldCount,
  FieldList,
  SaveRow,
  UnsavedIndicator,
  UnsavedDot,
  RightPanel,
  LoadingWrapper,
} from "./styles";

const { Title, Text } = Typography;

function Configuration() {
  const { isDarkMode } = useTheme();
  const [themeColors, setThemeColors] = useState<ThemeColors>(initialTheme);
  const [api, contextHolder] = notification.useNotification();

  const { data, isLoading, error } = useKioskConfiguration();
  const saveMutation = useSaveKioskConfiguration();

  // True as soon as any field drifts from the last value fetched/saved from
  // the server — the merchant's edits only live in the Live Preview until
  // they click Save.
  const isDirty = !!data && JSON.stringify(themeColors) !== JSON.stringify(data);

  useEffect(() => {
    if (data) setThemeColors(data);
  }, [data]);

  useEffect(() => {
    if (error) {
      api.error({
        message: "Error fetching configuration",
        description: (error as any)?.response?.data?.message || error.message,
      });
    }
  }, [error, api]);

  const handleColorChange = (key: keyof ThemeColors, value: string) => {
    setThemeColors((prev) => ({ ...prev, [key]: value }));
  };

  const handleClear = (key: keyof ThemeColors) => {
    setThemeColors((prev) => ({ ...prev, [key]: null }));
  };

  const handleSave = () => {
    saveMutation.mutate(themeColors, {
      onSuccess: () => {
        api.success({ message: "Theme configuration saved." });
      },
      onError: (err: any) => {
        api.error({
          message: "Error saving configuration",
          description: err?.response?.data?.message || err.message,
        });
      },
    });
  };

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? "dark" : "light" }}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <PageWrapper>
          {contextHolder}

          <LeftPanel>
            <PanelHeader>
              <Title level={4} style={{ margin: 0 }}>
                Kiosk Theme Configuration
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Click a swatch to pick a color, or type a hex code directly (e.g. #984343)
                into the box next to it. Colors left unset fall back to the kiosk app's
                built-in defaults.
              </Text>
              <div style={{ marginTop: 16 }}>
                <StoreSwitcher />
              </div>
            </PanelHeader>

            <ScrollableGroups>
              {isLoading ? (
                <LoadingWrapper>
                  <Spin size="large" />
                </LoadingWrapper>
              ) : COLOR_GROUPS.map((group) => (
                <GroupCard key={group.title}>
                  <GroupHeader $accent={group.accent}>
                    <GroupAccentBar $color={group.accent} />
                    <GroupTitle>{group.title}</GroupTitle>
                    <GroupFieldCount>{group.fields.length} colors</GroupFieldCount>
                  </GroupHeader>
                  <FieldList>
                    {group.fields.map(({ key, label, description }) => (
                      <ColorField
                        key={key}
                        fieldKey={key}
                        label={label}
                        description={description}
                        value={themeColors[key]}
                        onChange={handleColorChange}
                        onClear={handleClear}
                      />
                    ))}
                  </FieldList>
                </GroupCard>
              ))}
            </ScrollableGroups>

            <SaveRow>
              <div style={{ flex: 1, minWidth: 0 }}>
                {isDirty && (
                  <UnsavedIndicator>
                    <UnsavedDot />
                    Unsaved changes — click Save to apply them
                  </UnsavedIndicator>
                )}
              </div>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                size="large"
                loading={saveMutation.isPending}
                onClick={isLoading ? undefined : handleSave}
                style={isLoading ? { cursor: "not-allowed" } : undefined}
              >
                Save Theme Configuration
              </Button>
            </SaveRow>
          </LeftPanel>

          <RightPanel>
            <KioskPreview colors={themeColors} />
          </RightPanel>
        </PageWrapper>
      </ConfigProvider>
    </ThemeProvider>
  );
}

export default Configuration;
